import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, Mail, Bike, FileText, CheckCircle, XCircle,
  ToggleLeft, ToggleRight, Wallet, TrendingUp, TrendingDown,
  Package, Clock, ShoppingCart, Plus, Minus, MapPin, User,
  ChevronDown, ChevronUp, Truck, Calendar, IndianRupee, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useFetch } from '../hooks';
import { deliveryBoyService, walletService, orderService } from '../services/api';
import Modal from '../components/Modal';
import { formatCurrency, formatDate } from '../utils/helpers';
import { StatusBadge } from '../components/StatusBadge';

const TABS = ['Overview', 'Orders'];

/* ── Withdraw Status Badge ──────────────────────────────────────────────────── */
const WithdrawStatusBadge = ({ status }) => {
  const map = {
    pending:  { cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', icon: AlertCircle },
    approved: { cls: 'bg-green-100  text-green-800  dark:bg-green-900/40  dark:text-green-300',  icon: CheckCircle },
    rejected: { cls: 'bg-red-100    text-red-800    dark:bg-red-900/40    dark:text-red-300',    icon: XCircle     },
  };
  const { cls, icon: Icon } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cls}`}>
      <Icon className="w-3 h-3" />{status}
    </span>
  );
};

const ORDER_STATUSES = ['placed', 'approved', 'assigned', 'out-for-delivery', 'delivered', 'cancelled'];

const STATUS_COLORS = {
  placed:             'bg-sky-500 text-white',
  approved:           'bg-indigo-500 text-white',
  assigned:           'bg-violet-500 text-white',
  'out-for-delivery': 'bg-orange-500 text-white',
  delivered:          'bg-green-500 text-white',
  cancelled:          'bg-red-500 text-white',
};

const normalizeStatus = (s) => {
  if (!s) return s;
  if (s === 'confirmed')        return 'assigned';
  if (s === 'out_for_delivery') return 'out-for-delivery';
  if (s === 'delivery_failed')  return 'delivery-failed';
  return s;
};

/* ── Wallet Adjust Form ───────────────────────────────────────────────────── */
const WalletAdjustForm = ({ onSubmit, onClose }) => {
  const [type, setType] = useState('debit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    onSubmit({ type, amount: amt, note: note.trim() || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {['debit', 'credit'].map(t => (
          <button key={t} type="button" onClick={() => setType(t)}
            className={`py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
              type === t
                ? t === 'debit' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {t === 'debit' ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {t === 'debit' ? 'Deduct' : 'Credit'}
          </button>
        ))}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
        <input type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
          className="input-field" placeholder="e.g. 10.00" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (optional)</label>
        <input type="text" value={note} onChange={e => setNote(e.target.value)}
          className="input-field" placeholder="Reason for adjustment…" />
      </div>
      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit"
          className={`btn-primary ${type === 'debit' ? '!bg-red-500 hover:!bg-red-600' : ''}`}>
          {type === 'debit' ? 'Deduct' : 'Credit'} Amount
        </button>
      </div>
    </form>
  );
};

/* ── Order Detail Card (expandable) ──────────────────────────────────────── */
const OrderDetailCard = ({ order, onStatusUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleStatus = async (status) => {
    setUpdating(true);
    try {
      await onStatusUpdate(order.id, status);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      {/* Row — always visible */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors text-left"
      >
        <div className="w-9 h-9 bg-medihub-light dark:bg-medihub-dark/30 rounded-xl flex items-center justify-center shrink-0">
          <Package className="w-4 h-4 text-medihub-primary dark:text-medihub-highlight" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
              {order.uid || order.id}
            </span>
            <StatusBadge status={normalizeStatus(order.status)} dot={false} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {order.customer_name || order.patient} ·{" "}
            {formatDate(order.created_at || order.date)} ·{" "}
            {formatCurrency(order.total_amount || order.total)}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-gray-50 dark:bg-gray-800/50">
          {/* Info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              {
                icon: User,
                label: "Receiver",
                value: order.customer_name || order.patient || "—",
              },
              {
                icon: Phone,
                label: "Mobile",
                value: order.customer_mobile || order.mobile || "—",
              },
              {
                icon: MapPin,
                label: "Address",
                value:
                  [order.address_line, order.city, order.state, order.pincode]
                    .filter(Boolean)
                    .join(", ") ||
                  order.address ||
                  "—",
                span: true,
              },
              {
                icon: Calendar,
                label: "Order Date",
                value: formatDate(order.created_at || order.date),
              },
              {
                icon: IndianRupee,
                label: "Order Total",
                value: formatCurrency(order.total_amount || order.total),
              },
              {
                icon: IndianRupee,
                label: "COD Collected",
                value: formatCurrency(order.cod_collected || 0),
              },
              {
                icon: Wallet,
                label: "Payment",
                value:
                  order.payment_method === "cod"
                    ? "Cash on Delivery"
                    : "Online Payment",
              },
            ].map(({ icon: Icon, label, value, span }) => (
              <div
                key={label}
                className={`bg-white dark:bg-gray-700/60 rounded-lg p-3 ${span ? "col-span-2 sm:col-span-3" : ""}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {label}
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {value}
                </p>
              </div>
            ))}
          </div>

          {order.payment_method === "cod" && (
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                  parseFloat(order.cod_collected) > 0
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                }`}
              >
                <IndianRupee className="w-3 h-3" />
                {parseFloat(order.cod_collected) > 0
                  ? "COD Collected"
                  : "COD Not Collected"}
              </span>
            </div>
          )}

          {/* Medicine items */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Medicines
            </p>
            <div className="bg-white dark:bg-gray-700/60 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {["Medicine", "Qty", "Price", "Total"].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                  {order.items?.length > 0 ? (
                    order.items.map((item, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2.5 font-medium ...">
                          {item.medicine_name}
                        </td>
                        <td className="px-3 py-2.5 ...">{item.quantity}</td>
                        <td className="px-3 py-2.5 ...">
                          {formatCurrency(item.price)}
                        </td>
                        <td className="px-3 py-2.5 font-semibold ...">
                          {formatCurrency(
                            order.total_amount
                              ? item.price * item.quantity
                              : item.total,
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-4 text-center text-xs text-gray-400"
                      >
                        No items found
                      </td>
                    </tr>
                  )}
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td
                      colSpan={3}
                      className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400"
                    >
                      Grand Total
                    </td>
                    <td className="px-3 py-2.5 font-bold text-gray-900 dark:text-white">
                      {formatCurrency(order.total_amount || order.total || 0.0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Status update */}
          {(() => {
            const norm = normalizeStatus(order.status);
            const allowed =
              norm === "assigned"
                ? ["out-for-delivery"]
                : norm === "out-for-delivery"
                  ? ["delivered", "delivery-failed"]
                  : [];
            return allowed.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Update Status
                </p>
                <div className="flex flex-wrap gap-2">
                  {allowed.map((s) => (
                    <button
                      key={s}
                      disabled={updating}
                      onClick={() => handleStatus(s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors disabled:opacity-50 ${STATUS_COLORS[s] || "bg-gray-100 dark:bg-gray-700 text-gray-600"}`}
                    >
                      {s.replace(/-/g, " ")}
                    </button>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
};

/* ── Page ─────────────────────────────────────────────────────────────────── */
const DeliveryBoyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Overview');
  const [showAdjust, setShowAdjust] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const { data: boyData, loading, refetch } = useFetch(() => deliveryBoyService.getById(id), [id]);

  const boy          = boyData?.profile || null;
  const summary      = boyData?.summary || {};
  const codSummary   = boyData?.cod_summary || {};
  const orders       = boyData?.orders || [];

  const handleToggleStatus = async () => {
    const newStatus = boy.status === 'active' ? 'inactive' : 'active';
    try { await deliveryBoyService.toggleStatus(boy.id, newStatus); toast.success('Status updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const handleToggleVerified = async () => {
    const newVerified = boy.is_verified ? 0 : 1;
    try { await deliveryBoyService.toggleVerified(boy.id, newVerified); toast.success('Verification updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const handleWalletAdjust = async (payload) => {
    try {
      await walletService.adjustBalance(boy.id, payload);
      toast.success(`Wallet ${payload.type === 'credit' ? 'credited' : 'debited'} successfully`);
      setShowAdjust(false);
      refetch();
    } catch (err) { toast.error(err.message || 'Failed to adjust wallet'); }
  };

  const handleOrderStatusUpdate = async (orderId, status) => {
    try {
      await orderService.updateStatus(orderId, status);
      toast.success('Order status updated!');
      refetch();
    } catch { toast.error('Failed to update status'); }
  };

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      <div className="card p-6 h-40 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />
    </div>
  );

  if (!boy) return (
    <div className="text-center py-20">
      <p className="text-gray-500">Delivery boy not found.</p>
      <button onClick={() => navigate('/delivery-boys')} className="btn-primary mt-4">Back to List</button>
    </div>
  );

  const deliveryStats = [
    { label: 'Total Assigned', value: summary.total_assigned ?? 0, color: 'text-gray-900 dark:text-white',        bg: '' },
    { label: 'Delivered',      value: summary.delivered      ?? 0, color: 'text-green-700 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/10' },
    { label: 'Pending',        value: summary.pending        ?? 0, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/10' },
    { label: 'Cancelled',      value: summary.cancelled      ?? 0, color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/10' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/delivery-boys')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Delivery Partner Profile</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Detailed view, wallet & assigned orders</p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/40 rounded-2xl flex items-center justify-center text-orange-600 dark:text-orange-400 font-bold text-3xl shrink-0">
            {boy.name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{boy.name}</h2>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <StatusBadge status={boy.status} />
                  <StatusBadge status={boy.is_verified ? 'verified' : 'unverified'} />
                  {boy.bike_number && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-medihub-light text-medihub-secondary dark:bg-medihub-dark/40 dark:text-medihub-highlight">
                      <Bike className="w-3 h-3" />{boy.bike_number}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={handleToggleStatus}
                  className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                    boy.status === 'active'
                      ? 'border-green-300 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700'
                      : 'border-gray-300 text-gray-500 bg-gray-50 dark:bg-gray-700 dark:border-gray-600'
                  }`}
                >
                  {boy.status === 'active' ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  {boy.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={handleToggleVerified}
                  className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                    boy.is_verified
                      ? 'border-medihub-primary text-medihub-secondary bg-medihub-light dark:bg-medihub-dark/20 dark:text-medihub-highlight dark:border-medihub-accent'
                      : 'border-gray-300 text-gray-500 bg-gray-50 dark:bg-gray-700 dark:border-gray-600'
                  }`}
                >
                  {boy.is_verified ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {boy.is_verified ? 'Unverify' : 'Verify'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {[
                boy.email   && { icon: Mail,  label: boy.email },
                boy.mobile  && { icon: Phone, label: boy.mobile },
                boy.created_at && { icon: Clock, label: `Joined ${formatDate(boy.created_at)}` },
              ].filter(Boolean).map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Icon className="w-4 h-4 shrink-0 text-gray-400" />{label}
                </div>
              ))}
            </div>

            {/* Documents */}
            {(boy.adhar_front || boy.adhar_back || boy.pan_image || boy.license_image) && (
              <div className="mt-3">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Documents</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'Aadhaar Front', url: boy.adhar_front },
                    { label: 'Aadhaar Back',  url: boy.adhar_back },
                    { label: 'PAN Card',      url: boy.pan_image },
                    { label: 'License',       url: boy.license_image },
                  ].filter(d => d.url).map(({ label, url }) => (
                    <a key={label} href={url} target="_blank" rel="noreferrer"
                      className="group block border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-medihub-primary transition-colors">
                      <div className="h-20 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        {/\.(jpg|jpeg|png|webp)$/i.test(url)
                          ? <img src={url} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                          : <div className="w-full h-full flex items-center justify-center"><FileText className="w-6 h-6 text-gray-400" /></div>
                        }
                      </div>
                      <p className="text-xs text-center py-1.5 text-gray-600 dark:text-gray-400 font-medium">{label}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-4">
          <div className="flex gap-1">
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-medihub-primary text-medihub-primary dark:text-medihub-highlight'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab}
                {tab === 'Orders' && orders?.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-medihub-light dark:bg-medihub-dark/40 text-medihub-primary dark:text-medihub-highlight">
                    {orders.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5">
          {/* Overview */}
          {activeTab === 'Overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {deliveryStats.map(({ label, value, color, bg }) => (
                  <div key={label} className={`rounded-xl p-4 text-center border border-gray-100 dark:border-gray-700 ${bg}`}>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {summary.total_assigned > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Completion Rate</span>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400">
                      {Math.round((summary.delivered / summary.total_assigned) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.round((summary.delivered / summary.total_assigned) * 100)}%` }} />
                  </div>
                </div>
              )}

              {/* COD Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Total COD Amount',    value: formatCurrency(codSummary.total_cod_amount    ?? 0), color: 'text-gray-900 dark:text-white' },
                  { label: 'COD Collected',        value: formatCurrency(codSummary.total_cod_collected ?? 0), color: 'text-green-700 dark:text-green-400' },
                  { label: 'COD Pending',          value: formatCurrency(codSummary.total_cod_pending   ?? 0), color: 'text-orange-600 dark:text-orange-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                    <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Wallet */}
          {activeTab === 'Wallet' && (
            <div className="space-y-4">
              {/* 4 Summary Cards */}
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                {[
                  { label: 'Total Earnings',   value: formatCurrency(boy.totalEarnings ?? 0),  icon: TrendingUp,  color: 'text-green-600 dark:text-green-400',  bg: 'bg-green-50 dark:bg-green-900/20'   },
                  { label: 'Wallet Balance',   value: formatCurrency(boy.wallet ?? 0),        icon: Wallet,      color: 'text-medihub-primary dark:text-medihub-highlight',   bg: 'bg-medihub-light dark:bg-medihub-dark/20'    },
                  { label: 'Total Withdrawn',  value: formatCurrency(boy.totalWithdrawn ?? 0), icon: TrendingDown,color: 'text-orange-600 dark:text-orange-400',bg: 'bg-orange-50 dark:bg-orange-900/20' },
                  { label: 'Pending Requests', value: boy.withdrawRequests?.filter(r => r.status === 'pending').length ?? 0, icon: AlertCircle, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                  <div key={label} className="card p-5 flex items-center gap-4">
                    <div className={`p-3 rounded-xl shrink-0 ${bg}`}>
                      <Icon className={`w-6 h-6 ${color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
                      <p className={`text-xl font-bold ${color}`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Admin Adjust */}
              <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-medihub-light dark:bg-medihub-dark/30 rounded-xl flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-medihub-primary dark:text-medihub-highlight" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Admin Wallet Adjustment</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Manually credit or debit wallet balance</p>
                  </div>
                </div>
                <button onClick={() => setShowAdjust(v => !v)}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-medihub-primary hover:bg-medihub-secondary text-white rounded-lg text-sm font-medium transition-colors">
                  {showAdjust ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {showAdjust ? 'Cancel' : 'Adjust'}
                </button>
              </div>

              {showAdjust && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                  <WalletAdjustForm onSubmit={handleWalletAdjust} onClose={() => setShowAdjust(false)} />
                </div>
              )}

              {/* Earnings History */}
              <div className="card overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Earnings History</h3>
                  </div>
                </div>
                {!boy.walletTransactions?.filter(t => t.type === 'credit' && t.orderId).length ? (
                  <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No earnings yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          {['Order ID', 'Delivery Charge', 'Date'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {boy.walletTransactions.filter(t => t.type === 'credit' && t.orderId).map(e => (
                          <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs text-medihub-primary dark:text-medihub-highlight whitespace-nowrap">{e.orderId}</td>
                            <td className="px-4 py-3 font-semibold text-green-600 dark:text-green-400 whitespace-nowrap">{formatCurrency(e.amount)}</td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(e.date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Withdraw Requests */}
              <div className="card overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-orange-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Withdraw Requests</h3>
                  </div>
                  <button
                    onClick={() => { setWithdrawAmount(''); setWithdrawModal(true); }}
                    className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
                  >
                    <Plus className="w-3.5 h-3.5" />Request Withdraw
                  </button>
                </div>
                {!boy.withdrawRequests?.length ? (
                  <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No withdraw requests</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          {['Request ID', 'Amount', 'Status', 'Requested On', 'Processed On', 'Actions'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {boy.withdrawRequests.map(r => (
                          <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.id}</td>
                            <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(r.amount)}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <WithdrawStatusBadge status={r.status} />
                            </td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(r.date)}</td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{r.processedDate ? formatDate(r.processedDate) : '—'}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {r.status === 'pending' ? (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    // onClick={() => handleApproveWithdraw(r.id)}
                                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 transition-colors"
                                  >
                                    <CheckCircle className="w-3.5 h-3.5" />Approve
                                  </button>
                                  <button
                                    // onClick={() => handleRejectWithdraw(r.id)}
                                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 transition-colors"
                                  >
                                    <XCircle className="w-3.5 h-3.5" />Reject
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Transaction History */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">All Transactions</h3>
                {!boy.walletTransactions?.length ? (
                  <p className="text-sm text-gray-400 text-center py-8">No transactions yet</p>
                ) : (
                  <div className="space-y-2">
                    {boy.walletTransactions.map(txn => (
                      <div key={txn.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                            txn.type === 'credit' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                          }`}>
                            {txn.type === 'credit'
                              ? <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                              : <TrendingDown className="w-4 h-4 text-red-500 dark:text-red-400" />
                            }
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{txn.description}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {txn.orderId && (
                                <span className="text-xs font-mono text-medihub-primary dark:text-medihub-highlight bg-medihub-light dark:bg-medihub-dark/20 px-1.5 py-0.5 rounded">
                                  {txn.orderId}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{formatDate(txn.date)}</span>
                            </div>
                          </div>
                        </div>
                        <span className={`text-sm font-bold shrink-0 ${
                          txn.type === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                        }`}>
                          {txn.type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Orders */}
          {activeTab === 'Orders' && (
            <div className="space-y-3">
              {!orders?.length ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
                  <ShoppingCart className="w-10 h-10 opacity-40" />
                  <p className="text-sm font-medium">No orders found</p>
                </div>
              ) : (
                orders.map(order => (
                  <OrderDetailCard
                    key={order.id}
                    order={order}
                    onStatusUpdate={handleOrderStatusUpdate}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Request Withdraw Modal */}
      <Modal isOpen={withdrawModal} onClose={() => setWithdrawModal(false)} title="Request Withdraw" size="sm">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-medihub-light dark:bg-medihub-dark/20 rounded-xl">
            <span className="text-sm text-gray-600 dark:text-gray-400">Available Balance</span>
            <span className="font-bold text-medihub-primary dark:text-medihub-highlight">{formatCurrency(boy?.walletBalance ?? 0)}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Withdraw Amount</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={boy?.walletBalance}
              value={withdrawAmount}
              onChange={e => setWithdrawAmount(e.target.value)}
              className="input-field"
              placeholder="Enter amount…"
              autoFocus
            />
            {parseFloat(withdrawAmount) > (boy?.walletBalance ?? 0) && (
              <p className="text-xs text-red-500 mt-1">Amount exceeds wallet balance</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={() => setWithdrawModal(false)} className="btn-secondary">Cancel</button>
            <button
              // onClick={handleRequestWithdraw}
              disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > (boy?.walletBalance ?? 0)}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {withdrawing ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryBoyDetail;
